import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../../store';
import {
    fetchEstablishmentLocations,
    fetchSingleEstablishmentLocation,
    addEstablishmentLocation,
    updateEstablishmentLocation,
    deleteEstablishmentLocation,
    updateEstablishmentLocationOrder,
    DeleteEstablishmentLocation
} from './establishmentLocationAsyncThunk';
import { EstablishmentLocation } from '../../../types';
import { toast } from 'react-toastify';

interface EstablishmentLocationState {
    data: EstablishmentLocation[];
    selectedEstablishment: EstablishmentLocation | null;
    status: 'idle' | 'loading' | 'succeeded' | 'failed';
    loading: boolean;
    error: string | null;
}

const initialState: EstablishmentLocationState = {
    data: [],
    selectedEstablishment: null,
    status: 'idle',
    loading: false,
    error: null,
};

const establishmentLocationSlice = createSlice({
    name: 'establishmentLocation',
    initialState,
    reducers: {
        setSelectedEstablishment: (state, action: PayloadAction<EstablishmentLocation | null>) => {
            state.selectedEstablishment = action.payload;
        },
        clearEstablishmentLocations: (state) => {
            state.data = [];
            state.selectedEstablishment = null;
        },
    },
    extraReducers: (builder) => {
        // Fetch all
        builder
            .addCase(fetchEstablishmentLocations.pending, (state) => {
                state.status = 'loading';
                state.error = null;
            })
            .addCase(fetchEstablishmentLocations.fulfilled, (state, action: PayloadAction<EstablishmentLocation[]>) => {
                state.status = 'succeeded';
                state.data = action.payload;
            })
            .addCase(fetchEstablishmentLocations.rejected, (state, action) => {
                state.status = 'failed';
                toast.error(action.payload || 'Failed to fetch establishments');
                state.error = action.payload || 'Failed to fetch establishments';
            });

        // Fetch single
        builder
            .addCase(fetchSingleEstablishmentLocation.pending, (state) => {
                state.loading = true;
            })
            .addCase(fetchSingleEstablishmentLocation.fulfilled, (state, action: PayloadAction<EstablishmentLocation>) => {
                state.loading = false;
                state.selectedEstablishment = action.payload;
            })
            .addCase(fetchSingleEstablishmentLocation.rejected, (state, action) => {
                state.loading = false;
                toast.error(action.payload || 'Failed to fetch establishment');
                state.error = action.payload || 'Failed to fetch establishment';
            });

        // Add
        builder
            .addCase(addEstablishmentLocation.pending, (state) => {
                state.loading = true;
            })
            .addCase(addEstablishmentLocation.fulfilled, (state, action: PayloadAction<EstablishmentLocation>) => {
                state.loading = false;
                state.data.push(action.payload);
                toast.success('Establishment added successfully');
            })
            .addCase(addEstablishmentLocation.rejected, (state, action) => {
                state.loading = false;
                toast.error(action.payload || 'Failed to add establishment');
                state.error = action.payload || 'Failed to add establishment';
            });

        // Update
        builder
            .addCase(updateEstablishmentLocation.pending, (state) => {
                state.loading = true;
            })
            .addCase(updateEstablishmentLocation.fulfilled, (state, action: PayloadAction<EstablishmentLocation>) => {
                const index = state.data.findIndex((item) => item.id === action.payload.id);
                if (index !== -1) {
                    state.data[index] = action.payload;
                }
                if (state.selectedEstablishment?.id === action.payload.id) {
                    state.selectedEstablishment = action.payload;
                }
                state.loading = false;
                toast.success('Establishment updated successfully');
            })
            .addCase(updateEstablishmentLocation.rejected, (state, action) => {
                state.loading = false;
                toast.error(action.payload || 'Failed to update establishment');
                state.error = action.payload || 'Failed to update establishment';
            });

        // Delete
        builder
            .addCase(deleteEstablishmentLocation.pending, (state) => {
                state.loading = true;
            })
            .addCase(deleteEstablishmentLocation.fulfilled, (state, action: PayloadAction<DeleteEstablishmentLocation>) => {
                state.data = state.data.filter((item) => item.id !== action.payload.id);
                if (state.selectedEstablishment?.id === action.payload.id) {
                    state.selectedEstablishment = null;
                }
                state.loading = false;
                toast.success('Establishment deleted successfully');
            })
            .addCase(deleteEstablishmentLocation.rejected, (state, action) => {
                state.loading = false;
                toast.error(action.payload || 'Failed to delete establishment');
                state.error = action.payload || 'Failed to delete establishment';
            });

        // Update order
        builder
            .addCase(updateEstablishmentLocationOrder.pending, (state) => {
                state.status = 'loading';
                state.loading = true;
            })
            .addCase(updateEstablishmentLocationOrder.fulfilled, (state, action: PayloadAction<EstablishmentLocation[]>) => {
                state.data = action.payload;
                state.status = 'succeeded';
                state.loading = false;
                toast.success('Order updated successfully');
            })
            .addCase(updateEstablishmentLocationOrder.rejected, (state, action) => {
                state.status = 'failed';
                state.loading = false;
                toast.error(action.payload || 'Failed to update order');
                state.error = action.payload || 'Failed to update order';
            });
    },
});

export const { setSelectedEstablishment, clearEstablishmentLocations } = establishmentLocationSlice.actions;

export const selectEstablishmentLocation = (state: RootState) => state.establishmentLocation;

export default establishmentLocationSlice.reducer;
